import { AccountsContext } from '../../../../components/context/account-context';
import { getVendorExpenseReports } from '../../../../services/reports-service';
import { getUsersMinimal } from '../../../../services/users-service';
import { getAllVendorsMinimal } from '../../../../services/vendors-service';
import { getDateRangeUTC } from '../../../../utils/set-date-range-utils';
import React, { useContext, useEffect, useState } from 'react';
import Heading from '../../../../components/shared/heading';
import Loader from '../../../../components/shared/loader';
import DateRangePicker from '../../../../components/shared/date-range-picker';
import { IoSearchOutline } from 'react-icons/io5';
import CustomReactSelect from '../../../../components/shared/custom-react-selector';
import { prependDefault } from '../../../../utils/structure-options-utils';
import DataMap from '../data-map';
import Help from './help';
import { usePageHelp } from '../../../../components/context/help-context';

const ReportsExpensesVendor = () => {
    usePageHelp(Help);
    const { accountId } = useContext(AccountsContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
        endDate: new Date(),
    });
    const [error, setError] = useState(null);
    const [apiResponse, setApiResponse] = useState({ message: '', type: '' });
    const [users, setUsers] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [vendorId, setVendorId] = useState(null);
    const [userId, setUserId] = useState(null);
    const [expenseData, setExpenseData] = useState([]);

    useEffect(() => {
        const fetchGlobalData = async () => {
            if (!accountId) return;
            setLoading(true);
            setError(null);
            setApiResponse({ message: '', type: '' });

            try {
                const [usersRes, vendorsRes] = await Promise.all([
                    getUsersMinimal(accountId),
                    getAllVendorsMinimal(accountId),
                ]);

                if (usersRes.success) {
                    setUsers(usersRes.data.data || []);
                } else {
                    setError(usersRes.data.message || 'Failed to fetch users');
                }

                if (vendorsRes.success) {
                    setVendors(vendorsRes.data.data || []);
                } else {
                    setError(vendorsRes.data.message || 'Failed to fetch vendors');
                }
            } catch (err) {
                console.error('Failed to fetch global data', err);
                setError('Failed to fetch global data');
            } finally {
                setLoading(false);
            }
        };

        fetchGlobalData();
    }, [accountId]);

    const fetchExpenseReports = async () => {
        if (!accountId) return;
        setLoading(true);
        setError(null);
        setApiResponse({ message: '', type: '' });

        try {
            const { from, to } = getDateRangeUTC(dateRange);
            const response = await getVendorExpenseReports(accountId, from, to, vendorId, userId);
            if (response.success) {
                setExpenseData(response.data.data || []);
                setApiResponse({ message: 'Expense reports fetched successfully!', type: 'success' });
            } else {
                setError(response.data.message || 'Failed to fetch expense reports');
                setApiResponse({ message: response.data.message || 'Failed to fetch expense reports', type: 'error' });
            }
        } catch (err) {
            console.error('Failed to fetch expense reports', err);
            setError('An error occurred while fetching expense reports');
            setApiResponse({ message: 'An error occurred while fetching expense reports', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExpenseReports();
    }, [accountId, dateRange, vendorId, userId]);

    const handleSelectChange = (selectedOption, { name }) => {
        const value = selectedOption ? selectedOption.value : null;
        if (name === 'user') {
            setUserId(value);
        } else if (name === 'vendor') {
            setVendorId(value);
        }
        setApiResponse({ message: '', type: '' });
    };

    const vendorOptions = prependDefault(
        vendors.map((v) => ({ value: v.vendorId, label: v.label })),
        'Vendor'
    );
    const userOptions = prependDefault(
        users.map((s) => ({ value: s.userId, label: s.label })),
        'User'
    );

    return (
        <div className="px-6 py-4">
            <div className="mb-2">
                <Heading>Expense Report by Vendors</Heading>
            </div>

            <div className="flex flex-wrap md:flex-row flex-col items-center justify-between gap-3 my-5">
                <div className="flex gap-3 flex-wrap md:w-auto w-full justify-start">
                    <div className="bg-white rounded-md px-3 flex border border-border items-center gap-2 w-44 lg:w-72 py-1">
                        <IoSearchOutline className="text-gray-500 text-lg" />
                        <input
                            type="text"
                            className="bg-transparent outline-none py-1 w-full text-sm text-gray-700"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={loading}
                        />
                    </div>

                    <DateRangePicker
                        onChange={setDateRange}
                        initialRange={{
                            startDate: dateRange.startDate,
                            endDate: dateRange.endDate,
                        }}
                        disabled={loading}
                    />
                </div>

                <div className="flex gap-2 md:items-center md:w-auto w-full justify-end flex-1 lg:max-w-[20%] md:max-w-[20%]">
                    <CustomReactSelect
                        name="vendor"
                        value={vendorOptions.find((opt) => opt.value === vendorId) || null}
                        options={vendorOptions}
                        onChange={handleSelectChange}
                        isDisabled={loading}
                        placeholder=" Vendor"
                    />

                    <CustomReactSelect
                        name="user"
                        value={userOptions.find((opt) => opt.value === userId) || null}
                        options={userOptions}
                        onChange={handleSelectChange}
                        isDisabled={loading}
                        placeholder=" User"
                    />
                </div>
            </div>

            <div className="space-y-4">
                {(error || (apiResponse.message && apiResponse.type === 'error')) && (
                    <div className="p-3 rounded bg-red-100 text-red-700">
                        {error || apiResponse.message}
                    </div>
                )}

                <div>
                    {
                        loading ? <Loader /> :
                            <DataMap
                                data={expenseData}
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                                loading={loading}
                                type={"vendor"}
                            />
                    }
                </div>
            </div>
        </div>
    );
};

export default ReportsExpensesVendor;